import {
  GEO_SIM_QUERY,
  GEO_SIM_STORAGE_KEY,
  type GeoSimSession,
  type GeoSimVpnMode,
} from "./types";

function isVpnMode(v: unknown): v is GeoSimVpnMode {
  return v === "clean" || v === "anonymous" || v === "hosting";
}

export function readGeoSimSession(): GeoSimSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(GEO_SIM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeoSimSession>;
    if (
      !parsed ||
      typeof parsed.country !== "string" ||
      !/^[A-Za-z]{2}$/.test(parsed.country) ||
      typeof parsed.token !== "string" ||
      !parsed.token
    ) {
      return null;
    }
    return {
      country: parsed.country.toUpperCase(),
      state: parsed.state ?? null,
      province: parsed.province ?? null,
      city: parsed.city ?? null,
      lang: parsed.lang ?? null,
      forceVariant: parsed.forceVariant ?? null,
      simulateVpn: isVpnMode(parsed.simulateVpn) ? parsed.simulateVpn : "clean",
      simulateIp: parsed.simulateIp ?? null,
      token: parsed.token,
      activatedAt:
        typeof parsed.activatedAt === "number" ? parsed.activatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function writeGeoSimSession(session: GeoSimSession): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(GEO_SIM_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* private mode */
  }
}

export function clearGeoSimSession(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(GEO_SIM_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function isGeoSimUrlActive(search?: string): boolean {
  if (typeof window === "undefined" && search == null) return false;
  const q = new URLSearchParams(
    search ?? (typeof window !== "undefined" ? window.location.search : ""),
  );
  return q.get(GEO_SIM_QUERY.flag) === "1";
}

/** True if authorized geo-sim is active (URL flag or session storage). */
export function hasActiveGeoSim(): boolean {
  if (isGeoSimUrlActive()) return true;
  return readGeoSimSession() != null;
}

export type BuildGeoSimUrlInput = {
  path?: string;
  country: string;
  token: string;
  state?: string | null;
  province?: string | null;
  city?: string | null;
  lang?: string | null;
  forceVariant?: string | null;
  simulateVpn?: GeoSimVpnMode;
  simulateIp?: string | null;
  /** Preserve other query params from current location */
  preserveOther?: boolean;
};

export function buildGeoSimUrl(input: BuildGeoSimUrlInput): string {
  const path =
    input.path ??
    (typeof window !== "undefined" ? window.location.pathname || "/" : "/");
  const params = new URLSearchParams();
  if (input.preserveOther && typeof window !== "undefined") {
    const current = new URLSearchParams(window.location.search);
    current.forEach((v, k) => {
      if (
        k === GEO_SIM_QUERY.flag ||
        k === GEO_SIM_QUERY.token ||
        k === GEO_SIM_QUERY.country ||
        k === GEO_SIM_QUERY.state ||
        k === GEO_SIM_QUERY.province ||
        k === GEO_SIM_QUERY.city ||
        k === GEO_SIM_QUERY.lang ||
        k === GEO_SIM_QUERY.forceVariant ||
        k === GEO_SIM_QUERY.simulateVpn ||
        k === GEO_SIM_QUERY.simulateIp ||
        k === "debug"
      ) {
        return;
      }
      params.set(k, v);
    });
  }

  params.set(GEO_SIM_QUERY.flag, "1");
  params.set(GEO_SIM_QUERY.token, input.token);
  params.set(GEO_SIM_QUERY.country, input.country.toUpperCase().slice(0, 2));
  if (input.state?.trim()) params.set(GEO_SIM_QUERY.state, input.state.trim().toUpperCase());
  if (input.province?.trim()) params.set(GEO_SIM_QUERY.province, input.province.trim());
  if (input.city?.trim()) params.set(GEO_SIM_QUERY.city, input.city.trim());
  if (input.lang?.trim()) params.set(GEO_SIM_QUERY.lang, input.lang.trim());
  if (input.forceVariant?.trim()) {
    params.set(GEO_SIM_QUERY.forceVariant, input.forceVariant.trim());
  }
  params.set(GEO_SIM_QUERY.simulateVpn, input.simulateVpn || "clean");
  if (input.simulateIp?.trim()) {
    params.set(GEO_SIM_QUERY.simulateIp, input.simulateIp.trim());
  }

  const qs = params.toString();
  return `${path}?${qs}`;
}

/**
 * Re-attach missing geo_sim params via replaceState (SPA pin).
 * Does not hard-reload; edge config only updates on full navigation.
 */
export function pinGeoSimParamsIfNeeded(): boolean {
  if (typeof window === "undefined") return false;
  const session = readGeoSimSession();
  if (!session) return false;

  const url = new URL(window.location.href);
  const q = url.searchParams;
  let changed = false;

  if (q.get(GEO_SIM_QUERY.flag) !== "1") {
    q.set(GEO_SIM_QUERY.flag, "1");
    changed = true;
  }
  if (q.get(GEO_SIM_QUERY.token) !== session.token) {
    q.set(GEO_SIM_QUERY.token, session.token);
    changed = true;
  }
  if ((q.get(GEO_SIM_QUERY.country) || "").toUpperCase() !== session.country) {
    q.set(GEO_SIM_QUERY.country, session.country);
    changed = true;
  }
  if (session.state && q.get(GEO_SIM_QUERY.state) !== session.state) {
    q.set(GEO_SIM_QUERY.state, session.state);
    changed = true;
  }
  if (session.province && q.get(GEO_SIM_QUERY.province) !== session.province) {
    q.set(GEO_SIM_QUERY.province, session.province);
    changed = true;
  }
  if (session.city && q.get(GEO_SIM_QUERY.city) !== session.city) {
    q.set(GEO_SIM_QUERY.city, session.city);
    changed = true;
  }
  if (
    (q.get(GEO_SIM_QUERY.simulateVpn) || "clean") !==
    (session.simulateVpn || "clean")
  ) {
    q.set(GEO_SIM_QUERY.simulateVpn, session.simulateVpn || "clean");
    changed = true;
  }
  if (session.lang && q.get(GEO_SIM_QUERY.lang) !== session.lang) {
    q.set(GEO_SIM_QUERY.lang, session.lang);
    changed = true;
  }
  if (
    session.forceVariant &&
    q.get(GEO_SIM_QUERY.forceVariant) !== session.forceVariant
  ) {
    q.set(GEO_SIM_QUERY.forceVariant, session.forceVariant);
    changed = true;
  }
  if (
    session.simulateIp &&
    q.get(GEO_SIM_QUERY.simulateIp) !== session.simulateIp
  ) {
    q.set(GEO_SIM_QUERY.simulateIp, session.simulateIp);
    changed = true;
  }

  if (changed) {
    window.history.replaceState({}, "", `${url.pathname}?${q.toString()}${url.hash}`);
  }
  return changed;
}

/** Append geo-sim params onto an API query (cta-resolved poll, etc.). */
export function appendGeoSimToSearchParams(params: URLSearchParams): void {
  const session = readGeoSimSession();
  if (!session && !isGeoSimUrlActive()) return;

  const q =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const country =
    (q.get(GEO_SIM_QUERY.country) || session?.country || "").toUpperCase();
  const token = q.get(GEO_SIM_QUERY.token) || session?.token || "";
  if (!country || !token) return;

  params.set(GEO_SIM_QUERY.flag, "1");
  params.set(GEO_SIM_QUERY.token, token);
  // Session/URL sim wins over operator real geo (force, don't only fill if empty)
  params.set("country", country);
  const state = q.get(GEO_SIM_QUERY.state) || session?.state;
  const province = q.get(GEO_SIM_QUERY.province) || session?.province;
  const city = q.get(GEO_SIM_QUERY.city) || session?.city;
  if (state) params.set("state", state);
  if (province) params.set("province", province);
  if (city) params.set("city", city);
  params.set(
    GEO_SIM_QUERY.simulateVpn,
    q.get(GEO_SIM_QUERY.simulateVpn) || session?.simulateVpn || "clean",
  );
  const simIp = q.get(GEO_SIM_QUERY.simulateIp) || session?.simulateIp;
  if (simIp) params.set(GEO_SIM_QUERY.simulateIp, simIp);
}
